import React, { useCallback, useState } from "react";
import { IMaskInput } from "react-imask";
import "./birth-date-modal.sass";
import { updateChildInfo } from "../../util/children-helper";
import ModalWindow from "../ModalWindow";

import { Input, Typography } from "@mui/material";

import { ChildResponse } from "../../openapi";
import { useAlert } from "@/hooks/useAlert";
import { useTranslation } from "react-i18next";

interface MaskProps { }

const TextMaskInput = React.forwardRef<HTMLInputElement, MaskProps>((props, ref) => {
  // const BIRTHDATE = [/[0-9]/, /\d/, "/", /\d/, /\d/, /\d/, /\d/];
  return <IMaskInput ref={ref} {...props} mask="00/0000" name="birthYearMonth" required lazy={false} />;
});

TextMaskInput.displayName = "TextMaskInput";

interface ModalProps {
  showModal: boolean;
  setShowModal: (arg: boolean) => void;
  childrenList: ChildResponse[];
}

interface IChild {
  childId: string;
  firstName: string;
  birthYearMonth: string;
}

interface IError {
  childId: string;
  errorText: string;
}

const ModalBirthDate = ({ showModal, childrenList, setShowModal }: ModalProps) => {
  const [childData, setChildData] = useState<IChild[]>([]);
  const [error, setError] = useState<IError[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const alert = useAlert();
  const { t } = useTranslation("dashboard");

  const findChild = (child: ChildResponse): IChild | undefined => childData.find((prevChild) => prevChild.childId === child.id);

  const birthYearMonthValidation = useCallback((value: string): string | undefined => {
    if (!value) return undefined;

    if (!/^(0[1-9]|1[0-2])\/[0-9]{4}$/.test(value.trim())) {
      return t("pleaseEnterValidDate");
    }

    const [month, year] = value.split("/");
    const yearNum = Number(year);

    if (yearNum < 1921) {
      return t("yearMustBeGreater");
    }

    const inputDate = new Date(yearNum, Number(month) - 1);
    const currentDate = new Date();

    return inputDate < currentDate ? undefined : t("pleaseEnterValidDate");
  }, [t]);

  const handleValidations = (value: string, child: ChildResponse) => {
    const errorText = value === "" ? t("required", { ns: "common" }) : birthYearMonthValidation(value);
    if (errorText) {
      setError([...error.filter((prev) => prev.childId !== child.id), { childId: child.id, errorText }]);
    } else {
      setError(error.filter((prev) => prev.childId !== child.id));
    }
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, child: ChildResponse) => {
    handleValidations(e.target.value, child);
    const data = {
      childId: child.id,
      firstName: child.firstName,
      birthYearMonth: e.target.value,
    };
    setChildData((prev) => [...prev.filter((prevChild) => prevChild.childId !== child.id), data]);
  };

  const getError = (childId: string): IError | undefined => error.find((item) => item.childId === childId);

  const saveBirthYearMonth = async (child: IChild) => {
    const yearMonthSplitted = child.birthYearMonth.split("/");
    return await updateChildInfo(child.childId, {
      ...child,
      birthYearMonth: `${yearMonthSplitted[1]}-${yearMonthSplitted[0]}`,
    });
  };

  const isButtonDisabled = childData.length !== childrenList.length || error.length > 0;

  const handleSave = async (): Promise<void> => {
    setErrorMessage("");
    await Promise.all(
      childData.map(async (child) => {
        const updatedChildren = await saveBirthYearMonth(child);
        if (!updatedChildren) setErrorMessage(t("errorUpdatingBirthday"));
      }),
    );
    alert.success(t("birthDateUpdated"));
  };

  const getModalContent = () => (
    <div className="birth-year-month">
      <p>{t("provideBirthdayInfo", { child: childrenList.length === 1 ? "child's" : "children's" })}</p>

      <div className="date-input-wrapper">
        {errorMessage ? (
          <Typography paragraph={true} sx={{ color: "error.main", px: 1, mb: 0 }}>
            {errorMessage}
          </Typography>
        ) : null}
        {childrenList.map((child) => (
          <div key={child.id} className="date-input-container">
            <label>{child.firstName}</label>
            <Input
              value={findChild(child) ? findChild(child)?.birthYearMonth : ""}
              onChange={(e) => handleOnChange(e, child)}
              id="formatted-text-mask-input-2"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              inputComponent={TextMaskInput as any}
              margin="dense"
            />
            <Typography paragraph={true} sx={{ color: "error.main", pt: 2 }}>
              {getError(child.id)?.errorText}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <ModalWindow
      headerText={t("whatIsChildBirthday")}
      showModal={showModal}
      setShowModal={setShowModal}
      renderBody={getModalContent}
      onActionButtonPress={handleSave}
      actionButtonType="primary"
      actionButtonText={t("done", { ns: "common" })}
      confirmDisabled={isButtonDisabled}
    />
  );
};

export default ModalBirthDate;
